import React, { useContext, useRef, useState } from "react";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  Typography,
  Box,
  IconButton,
  Chip,
} from "@material-ui/core";

import {
  BugReport,
  EmojiObjects,
  AttachFile,
  Close,
} from "@material-ui/icons";

import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

// Versao da app frontend — sync com frontend/package.json.
// Acompanha o backend nos relatorios pra distinguir versoes em prod.
const APP_VERSION = "1.2.8";

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB

const useStyles = makeStyles(theme => ({
  meta: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.action.hover,
    borderRadius: 6,
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
  metaRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    overflow: "hidden",
  },
  metaLabel: {
    fontWeight: 600,
    minWidth: 70,
  },
  metaValue: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  radioRow: {
    flexDirection: "row",
    marginBottom: theme.spacing(1),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  fileChip: {
    maxWidth: "100%",
  },
  hiddenInput: {
    display: "none",
  },
}));

const FeedbackModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  const [type, setType] = useState("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setType("bug");
    setTitle("");
    setDescription("");
    setFile(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handlePickFile = e => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_FILE_BYTES) {
      toast.warn(i18n.t("feedback.errors.fileTooLarge"));
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      toast.warn(i18n.t("feedback.errors.required"));
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("type", type);
      form.append("title", title.trim());
      form.append("description", description.trim());
      form.append("url", window.location.href);
      form.append("version", APP_VERSION);
      if (file) form.append("attachment", file);

      await api.post("/feedback", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(i18n.t("feedback.success"));
      reset();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="feedback-dialog-title"
    >
      <DialogTitle id="feedback-dialog-title">
        {i18n.t("feedback.title")}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <div className={classes.meta}>
            <div className={classes.metaRow}>
              <span className={classes.metaLabel}>
                {i18n.t("feedback.meta.user")}:
              </span>
              <span className={classes.metaValue}>
                {user?.name} &lt;{user?.email}&gt;
              </span>
            </div>
            <div className={classes.metaRow}>
              <span className={classes.metaLabel}>
                {i18n.t("feedback.meta.url")}:
              </span>
              <span className={classes.metaValue}>
                {window.location.href}
              </span>
            </div>
            <div className={classes.metaRow}>
              <span className={classes.metaLabel}>
                {i18n.t("feedback.meta.version")}:
              </span>
              <span className={classes.metaValue}>{APP_VERSION}</span>
            </div>
          </div>

          <Typography variant="body2" gutterBottom>
            {i18n.t("feedback.typeLabel")}
          </Typography>
          <RadioGroup
            value={type}
            onChange={e => setType(e.target.value)}
            className={classes.radioRow}
          >
            <FormControlLabel
              value="bug"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gridGap={4}>
                  <BugReport fontSize="small" />
                  {i18n.t("feedback.types.bug")}
                </Box>
              }
            />
            <FormControlLabel
              value="improvement"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gridGap={4}>
                  <EmojiObjects fontSize="small" />
                  {i18n.t("feedback.types.improvement")}
                </Box>
              }
            />
          </RadioGroup>

          <TextField
            className={classes.field}
            variant="outlined"
            fullWidth
            label={i18n.t("feedback.fields.title")}
            value={title}
            onChange={e => setTitle(e.target.value)}
            inputProps={{ maxLength: 200 }}
            required
          />

          <TextField
            className={classes.field}
            variant="outlined"
            fullWidth
            multiline
            minRows={4}
            label={i18n.t("feedback.fields.description")}
            value={description}
            onChange={e => setDescription(e.target.value)}
            inputProps={{ maxLength: 3800 }}
            required
          />

          <div className={classes.fileRow}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handlePickFile}
              className={classes.hiddenInput}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<AttachFile />}
              onClick={() => fileInputRef.current?.click()}
            >
              {i18n.t("feedback.fields.attach")}
            </Button>
            {file && (
              <Chip
                className={classes.fileChip}
                label={`${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)`}
                onDelete={() => setFile(null)}
                deleteIcon={<Close />}
                size="small"
              />
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            {i18n.t("feedback.buttons.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={submitting}
          >
            {submitting
              ? i18n.t("feedback.buttons.sending")
              : i18n.t("feedback.buttons.send")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FeedbackModal;
